import React, { useEffect } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { Alert, Col, Form, Input, notification, Row, Tooltip } from 'antd';
import moment from 'moment';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import { CheckboxSegmented } from '@src/components/CheckboxSegmented';
import FilterContainer from '@src/components/FilterContainer';
import TimeZone from '@src/components/TimeZone';
import { FORM_LAYOUT_DEFAULT } from '@src/constants/commons';
import { ALLOWED_ROLES, mergeAllowedRoles } from '@src/packages/utils';
import { getFullUrl } from '@src/packages/utils/functions';
import ShiftService from '@src/services/ShiftService';
import IntlMessages from '@src/util/IntlMessages';

import ActiveDay, { createRecurrenceStartBreakRule } from '../../components/ActiveDay';
import ShiftPermission from '../../components/ShiftPermission';
import { DEFAULT_ACTIVE_DAY, DEFAULT_DAYS_OPTIONS, PATHS, SHIFT_STATUS } from '../../constants';
import { useShiftValidation } from '../../hooks/useShiftValidation';
import { useShiftValidationTranslations } from '../../hooks/useShiftValidationTranslations';
import type {
  ActiveDaySerialized,
  OrganizationsReduxState,
  WorkJourneySummary,
} from '../../types';
import { buildRegulationName } from '../utils';

const { Item } = Form;

export default function ShiftFixed() {
  const [loading, setLoading] = React.useState(false);
  const [workJourneys, setWorkJourneys] = React.useState<WorkJourneySummary[]>([]);
  const navigate = useHistory();
  const intl = useIntl();
  const [form] = Form.useForm();
  const organizationId = useSelector(
    ({ organizations }: OrganizationsReduxState) => organizations?.organization?.id,
  );
  const { shiftId } = useParams<{ shiftId?: string }>();
  const shiftService = React.useMemo(() => new ShiftService(), []);
  const hasAssociatedJourneys = workJourneys.length > 0 && !!shiftId;

  const {
    hoursByWeek,
    warnings,
    validateDaysRule,
    errors,
    validateDays,
    validateActiveDay,
    handleFormValuesChange,
    hasErrors,
    hasWarnings,
    calculateTotalShiftHours,
    validateSundaysRule,
    validateSunday,
  } = useShiftValidation('fixed');
  const validationMessages = useShiftValidationTranslations();

  const onSave = async () => {
    try {
      const values = form.getFieldsValue();

      if (hasErrors()) {
        notification.error({
          description: intl.formatMessage({ id: 'general.hasErrors.message' }),
        } as Parameters<typeof notification.error>[0]);
        return;
      }

      setLoading(true);

      if (shiftId) {
        // Update logic
        await shiftService.updateFixedShift({
          ...values,
          status: SHIFT_STATUS.ACTIVE,
          organizationId,
          id: shiftId,
        });
      } else {
        // Create logic
        await shiftService.createFixedShift({
          ...values,
          status: SHIFT_STATUS.ACTIVE,
          organizationId,
        });
      }
      notification.success({
        message: intl.formatMessage({ id: 'general.save.successful.message' }),
      });
      navigate.push(getFullUrl(PATHS.BASE_URL, window.location.pathname));
    } catch (error) {
      notification.error({
        message: intl.formatMessage({ id: 'general.save.error.message' }),
      });
    } finally {
      setLoading(false);
    }
  };

  const getWorkJourneysByShiftId = async (shiftId: string, orgId: string) => {
    const workJourneys = await shiftService.getWorkJourneysByShiftId(shiftId, orgId);
    setWorkJourneys(workJourneys);
    return workJourneys;
  };

  useEffect(() => {
    if (!shiftId) return;
    const fetchShift = async () => {
      setLoading(true);
      const shift = await shiftService.getShift(shiftId, organizationId);
      if (!shift) return;
      // Find the first day object in the recurrence weeks that exists
      const firstDayFound: Partial<ActiveDaySerialized> =
        shift?.recurrence?.weeks?.flatMap(week => Object.values(week.days))?.find(day => !!day) ||
        {};
      const recurrenceData = {
        isActive: firstDayFound.isActive ?? true,
        startTime: firstDayFound.startTime
          ? moment(firstDayFound.startTime, 'HH:mm')
          : moment('08:00', 'HH:mm'),
        endTime: firstDayFound.endTime
          ? moment(firstDayFound.endTime, 'HH:mm')
          : moment('17:00', 'HH:mm'),
        breakTime: firstDayFound.breakTime ?? null,
        startBreakTime: firstDayFound.startBreakTime
          ? moment(firstDayFound.startBreakTime, 'HH:mm')
          : null,
      };
      let resolution = null;
      if (shift?.resolution?.name || shift?.resolution?.date) {
        resolution = {
          name: shift?.resolution?.name,
          date:
            shift?.resolution?.date && shift.resolution.date.seconds
              ? moment.unix(shift.resolution.date.seconds)
              : null,
        };
      }
      form.setFieldsValue({
        recurrence: recurrenceData,
        days: shift.daysOfWeek,
        name: shift.name,
        regulationName: shift.regulationName,
        timeZone: shift.timeZone,
        resolution,
      });
      calculateTotalShiftHours(shift.daysOfWeek, recurrenceData);
      // Sync warning state that form validators won't trigger on programmatic setFieldsValue
      validateSunday(shift.daysOfWeek);
      validateDays(shift.daysOfWeek);
      setLoading(false);
    };
    fetchShift();
    getWorkJourneysByShiftId(shiftId, organizationId);
  }, [shiftId, organizationId, form, shiftService, calculateTotalShiftHours]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <BoxContainer>
      <BoxContainer content fixed bottomBorder>
        <FilterContainer
          goBack={() => navigate.goBack()}
          title={<Title.Header value={<IntlMessages id="shifts.create.fixed.title" />} />}
          buttonItems={[
            {
              iconName: 'save',
              action: () => form.submit(),
              disabled: loading,
              allowedRole: mergeAllowedRoles(
                ALLOWED_ROLES.ORGANIZATIONS.WORK_SHIFTS.CREATE,
                ALLOWED_ROLES.ORGANIZATIONS.WORK_SHIFTS.UPDATE,
              ),
            },
          ]}
        />
      </BoxContainer>
      <BoxContainer content loading={loading}>
        <Form
          form={form}
          name="shiftFixed"
          layout={FORM_LAYOUT_DEFAULT as unknown as React.ComponentProps<typeof Form>['layout']}
          onValuesChange={(changedFields, allFields) => {
            handleFormValuesChange(changedFields, allFields, form.setFieldValue);
            // Call buildRegulationName independently
            buildRegulationName(allFields, form.setFieldValue);
          }}
          onFinish={onSave}
          initialValues={{
            weekCount: null,
            days: [],
            recurrence: {
              ...DEFAULT_ACTIVE_DAY,
            },
            name: '',
            regulationName: '',
            timeZone: '',
            resolution: {
              name: '',
              date: '',
            },
          }}
        >
          {hasAssociatedJourneys && (
            <Row>
              <Alert
                message={intl.formatMessage({ id: 'shifts.edit.hasAssociatedJourneys' })}
                type="warning"
                showIcon
              />
            </Row>
          )}
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <Title.SubTitle value={<IntlMessages id="shifts.recurrence" />} />
            </Col>
            <Col span={24}>
              <Title.LabelForm
                required
                className="gx-mb-2"
                value={<IntlMessages id="shifts.form.recurrence" />}
              />
              <Item
                name="days"
                rules={[
                  {
                    required: true,
                    message: validationMessages.daysRequired,
                  },
                  validateDaysRule(),
                  validateSundaysRule(),
                ]}
              >
                <CheckboxSegmented
                  disabled={hasAssociatedJourneys}
                  onChange={validateDays}
                  options={DEFAULT_DAYS_OPTIONS}
                />
              </Item>
            </Col>
            <Col xl={12} lg={12} md={12} sm={24} xs={24}>
              <Item name="recurrence" rules={[createRecurrenceStartBreakRule(intl)]}>
                <ActiveDay
                  warnings={warnings}
                  errors={errors}
                  onChange={v => validateActiveDay(v)}
                  disabled={hasAssociatedJourneys}
                />
              </Item>
            </Col>
            <Col className="gx-mb-2" span={24}>
              <div className="gx-flex-col gx-gap-2">
                <div className="gx-font-weight-bold">
                  {intl.formatMessage(
                    { id: 'shift.weeklyEffectiveHours' },
                    { hours: hoursByWeek.formated },
                  )}
                </div>
                {warnings.maxHours.length > 0 && (
                  <div className="gx-text-warning gx-w-100 gx-w-sm-50">
                    {warnings.maxHours.map(warning => warning.message).join(', ')}
                  </div>
                )}
              </div>
            </Col>
            <Col xs={24} md={12} lg={12} xl={12}>
              <Title.LabelForm
                required
                className="gx-mb-2"
                value={<IntlMessages id="shifts.form.timezone" />}
              />
              <Item
                name="timeZone"
                rules={[
                  { required: true, message: validationMessages.timeZoneRequired },
                ]}
              >
                <TimeZone className="gx-w-100" disabled={hasAssociatedJourneys} />
              </Item>
            </Col>
          </Row>
          <Row gutter={[8, 8]}>
            <Col span={24}>
              <Title.SubTitle value={<IntlMessages id="shift.name" />} />
            </Col>
            <Col xs={24} md={12} lg={12} xl={12}>
              <Title.LabelForm
                className="gx-mb-2"
                required
                value={<IntlMessages id="shifts.form.name" />}
              />
              <Item
                name="name"
                rules={[
                  { required: true, message: validationMessages.nameRequired },
                ]}
              >
                <Input />
              </Item>
            </Col>
            <Col xs={24} md={12} lg={12} xl={12}>
              <Title.LabelForm
                className="gx-mb-2"
                value={<IntlMessages id="shifts.form.regulation.name" />}
              />
              <Item noStyle shouldUpdate={(prev, next) => prev.recurrence !== next.recurrence}>
                {({ getFieldValue }) => (
                  <Tooltip title={<div>{getFieldValue('regulationName')}</div>}>
                    <Item name="regulationName">
                      <Input disabled />
                    </Item>
                  </Tooltip>
                )}
              </Item>
            </Col>
          </Row>
          <ShiftPermission
            show={
              hasWarnings() ||
              !!form.getFieldValue('resolution')?.name ||
              !!form.getFieldValue('resolution')?.date
            }
          />
        </Form>
      </BoxContainer>
    </BoxContainer>
  );
}
